import { Location } from '@angular/common';
import { Injectable, Injector, inject, runInInjectionContext } from '@angular/core';
import { HashStorageService } from '@df/browser/storage/hash-storage.service';
import { Attribute } from '@df/catalog/attribute/attribute';
import { AttributeResource } from '@df/catalog/attribute/attribute.resource';
import { IModelDataV3WithUrl } from '@df/catalog/model';
import { IProductDataV3, Product } from '@df/catalog/product/product';
import { Meta } from '@df/core/meta/meta';
import { NProductConfiguration } from '@df/module-product-configuration/df-module-product-configuration.interfaces';
import defaultsDeep from '@df/util/fn/defaults-deep';
import { StandardSerializer } from '@df/util/fn/standard-serializer';
import difference from 'lodash-es/difference';
import isEmpty from 'lodash-es/isEmpty';
import without from 'lodash-es/without';
import { BehaviorSubject, Subject } from 'rxjs';
import { ProductConfiguration } from '../product-configuration';
import { IProductConfigurationOptions } from './../product-configuration';
import { ProductConfigurationConfigurableOptionsService } from './options/product-configuration-configurable-options.service';
import { ProductConfigurationConfigurableProductDataService } from './product-data/product-configuration-configurable-product-data.service';
import { ProductConfigurationConfigurableStorageService } from './storage/product-configuration-configurable-storage.service';
import {
    ATTRIBUTE_TOKEN,
    ProductConfigurationConfigurableOptionValue,
    VALUE_TOKEN
} from './options/product-configuration-configurable-option-value';

interface IBareOptionsMapEntry {
    attribute: Attribute;
    values: number[];
}

export interface IOptionsMapEntry {
    attribute: Attribute;
    values: ProductConfigurationConfigurableOptionValue[];
}

export type IBareOptionsMap = IBareOptionsMapEntry[];
export type IOptionsMap = IOptionsMapEntry[];

interface ISelectionChangeEvent {
    selection: NProductConfiguration.Configurable.Selection;
    change: Partial<NProductConfiguration.Configurable.Selection>;
}

export interface IMatchResult {
    matchChildrenResult: NProductConfiguration.Configurable.ChildrenMatchResult;
    bestMatch: Product<IProductDataV3> | undefined;
}

@Injectable()
export class ProductConfigurationConfigurable extends ProductConfiguration {
    /**
     * Option instance class
     */
    static optionInstance = ProductConfigurationConfigurableOptionValue;

    /**
     * Configuration type
     */
    readonly type = 'configurable';

    /**
     * Configurable product selection model
     */
    readonly selection$ = new BehaviorSubject<NProductConfiguration.Configurable.Selection>({});

    /**
     * Selection mutation event
     * Selection object remains the same but its values are modified
     */
    readonly selectionChange$ = new Subject<ISelectionChangeEvent>();

    /**
     * Parent product
     */
    readonly parent$ = new BehaviorSubject<Product<IProductDataV3>>(undefined as any);

    /**
     * Options
     */
    readonly bareOptions$ = new BehaviorSubject<IBareOptionsMap>(undefined as any);

    /**
     * Options
     */
    readonly options$ = new BehaviorSubject<IOptionsMap>(undefined as any);

    /**
     * Options service
     */
    private optionsService = inject(ProductConfigurationConfigurableOptionsService);

    /**
     * Storage service
     */
    private storageService = inject(ProductConfigurationConfigurableStorageService);

    /**
     * Product data service
     */
    private dataService = inject(ProductConfigurationConfigurableProductDataService);

    /**
     * Location service
     */
    private location = inject(Location);

    /**
     * Hash storage service
     */
    private hashStorageService = inject(HashStorageService);

    /**
     * Attribute resource
     */
    private attributeResource = inject(AttributeResource);

    /**
     * Meta service
     */
    private meta = inject(Meta);

    /**
     * Injector
     */
    private injector = inject(Injector);

    /**
     * Module config
     */
    private moduleConfig = inject(NProductConfiguration.OPTIONS);

    /**
     * Configuration
     */
    private config?: NProductConfiguration.Configurable.Config;

    /**
     * If init method is finished
     */
    private initiated = false;

    private optionValuesMap = new Map<number, ProductConfigurationConfigurableOptionValue>();

    override init(
        product: Product<IProductDataV3>,
        options: IProductConfigurationOptions,
        config?: Partial<NProductConfiguration.Configurable.Config>
    ) {
        // initialize product and config
        this.config = defaultsDeep(config || {}, this.moduleConfig.configurable) as NProductConfiguration.Configurable.Config;
        this.dataService.init(this.config!, this);
        super.init(product, options);

        // initialize selection
        const selection = this.storageService.read(this.config!.hashStorageKey) || {};
        if (product.isSimple && isEmpty(selection)) {
            this.dataService.fillBySimple(selection, product, true);
        }

        // update options on bare options change
        this.subscriptions.add(this.bareOptions$.subscribe(() => this.updateOptions()));

        // sync options with currently selected parent
        this.subscriptions.add(
            this.parent$.subscribe(parent => {
                if (parent) {
                    const options = this.optionsService.getOptionsMap(parent);
                    if (this.bareOptions$.value !== options) {
                        this.bareOptions$.next(options);
                    }
                    if (this.initiated) {
                        this.onSelectionChange(
                            { selection: this.selection$.value, change: {} },
                            this.getSelectionLogic(this.config!.selection.parentChange)
                        );
                    }
                }
            })
        );

        this.dataService.sanitize(selection, product.parent as Product<IProductDataV3>);
        this.selection$.next(selection);

        this.subscriptions.add(
            this.selectionChange$.subscribe((event: ISelectionChangeEvent) => {
                this.dataService.sanitize(event.selection, this.parent$.value);
                this.updateOptions();
                this.onSelectionChange(event, this.getSelectionLogic(this.config!.selection.selectionChange));
            })
        );

        // init product to match selection
        this.onSelectionChange({ selection: this.selection$.value, change: {} }, this.getSelectionLogic());

        //
        this.initiated = true;
    }

    /**
     * Gets configuration link
     */
    override getUrl(): string | undefined {
        const hashParams = this.storageService.getHashParams(this.selection$.value);
        const path = this.product$.value?.data?.url;
        const hash = hashParams ? StandardSerializer.serialize(hashParams, '', undefined, { noescape: true }) : undefined;
        return hash ? `${path}#${hash}` : path;
    }

    match(changedAttributes: string[], selection: NProductConfiguration.Configurable.Selection = this.selection$.value): IMatchResult {
        const unchangedAttributes = difference(this.options$.value?.map(i => i.attribute.id) || [], changedAttributes);

        const matchChildrenResult = this.dataService.matchChildren(selection, this.parent$.value, [
            ...changedAttributes,
            ...unchangedAttributes
        ]);

        return {
            matchChildrenResult,
            bestMatch: this.config!.chooseChild(matchChildrenResult, selection)
        };
    }

    /**
     * Selection change event handler
     *
     * Saves selection to URL
     * Selects product matching the selection
     */
    private onSelectionChange(event: ISelectionChangeEvent, logic: NProductConfiguration.Configurable.SelectionLogic) {
        const selection = event.selection;
        const { matchChildrenResult, bestMatch } = this.match(Object.keys(event.change), selection);

        // partial or full match
        if (bestMatch) {
            // mismatch (conflicting attrs)
            if (matchChildrenResult.omittedAttributes.length > 0) {
                switch (logic.onMismatch) {
                    case 'change':
                        this.dataService.fillBySimple(selection, bestMatch);
                        matchChildrenResult.undefinedAttributes.forEach(attrCode => (selection[attrCode] = undefined));
                        this.setProduct(bestMatch);
                        break;
                    case 'unselect':
                        this.dataService.fillBySimple(selection, bestMatch);
                        [...matchChildrenResult.omittedAttributes, ...matchChildrenResult.undefinedAttributes].forEach(
                            attrCode => (selection[attrCode] = undefined)
                        );
                        this.setProduct(bestMatch.parent as Product<IProductDataV3>);
                        break;
                    case 'prevent':
                        return;
                }
            }
            // exact match; we switch child to
            else if (!matchChildrenResult.undefinedAttributes.length) {
                this.setProduct(bestMatch);
            } else if (logic.onPartial === 'simple') {
                this.dataService.fillBySimple(selection, bestMatch);
                this.setProduct(bestMatch);
            }
            // partial match; we may need to switch parent
            else {
                this.setProduct(bestMatch.parent as Product<IProductDataV3>);
            }
        }
        // no match at all
        else {
            if (logic.onMismatch === 'prevent') {
                return;
            } else {
                this.setProduct(this.parent$.value);
            }
        }
        this.storageService.write(selection, this.config!.hashStorageKey);
    }

    override validate(): NProductConfiguration.Error.Entry[] | undefined {
        const errors: NProductConfiguration.Error.Entry[] = [];
        if (this.product$.value.isConfigurable) {
            const selection = this.selection$.value;
            let notSelectedCodes = Object.keys(selection).filter(key => !selection[key]);
            const order = this.config?.order;
            if (order) {
                notSelectedCodes = [
                    ...order.filter(i => notSelectedCodes.includes(i)),
                    ...notSelectedCodes.filter(i => !order.includes(i))
                ];
            }
            if (notSelectedCodes.length) {
                errors.push(
                    ...notSelectedCodes.map(
                        (code): NProductConfiguration.Error.Entry => ({
                            level: NProductConfiguration.Error.Level.error,
                            code: NProductConfiguration.Error.Code.missing,
                            fieldName: this.attributeResource.get(code)?.data?.label || code
                        })
                    )
                );
            }
        }
        errors.push(...(super.validate() || []));
        return errors.length ? errors : undefined;
    }

    protected override onProductChange(product?: Product<IProductDataV3>) {
        if (product) {
            const parent = product.parent as Product<IProductDataV3>;
            if (!parent.isConfigurable || !parent.data) {
                throw new Error('Product parent is not configurable are could not be loaded');
            }
            if (this.parent$.value !== parent) {
                this.parent$.next(parent);
            }
            this.saveMetaState();
        }
        super.onProductChange(product);
    }

    private updateOptions() {
        let bareOptions = this.bareOptions$.value;
        if (!bareOptions) {
            return;
        }

        // sort options if needed
        const order = this.config?.order;
        if (order) {
            const sorted = order
                .map(attributeCode => bareOptions.find(option => option.attribute.id === attributeCode))
                .filter(i => !!i) as IBareOptionsMap;
            bareOptions = [...sorted, ...without(bareOptions, ...sorted)];
        }

        // apply values filters
        const engines = this.config?.optionsEngines || [];

        const options = bareOptions.map(option => {
            let displayedValues: ProductConfigurationConfigurableOptionValue[] = [];
            option.values.forEach(value => {
                let state: NProductConfiguration.Configurable.EngineResult;
                engines.forEach(engine => {
                    if (state === undefined) {
                        state = engine(option.attribute.id, value);
                    }
                });
                if (state !== 'exclude') {
                    const optionValue = this.retrieveOptionValue(option.attribute, value);
                    optionValue.setStateError(!state || state === 'include' ? undefined : state);

                    displayedValues.push(optionValue);
                }
            });

            return {
                ...option,
                values: displayedValues
            };
        });

        this.options$.next(options);
    }

    /**
     * Retrieves selection for given attribute
     */
    getSelection(attributeCode: string): number | undefined {
        return this.selection$.value[attributeCode];
    }

    /**
     * Checks if attribute is selected
     */
    hasSelection(attributeCode: string): boolean {
        return this.getSelection(attributeCode) !== undefined;
    }

    /**
     * Checks if given value is selected
     */
    isSelected(attributeCode: string, value: number): boolean {
        return this.getSelection(attributeCode) === value;
    }

    /**
     * Selects a value
     */
    select(attributeCode: string, value: number) {
        if (!this.isSelected(attributeCode, value)) {
            const selection = this.selection$.value;
            selection[attributeCode] = value;
            this.selectionChange$.next({
                selection,
                change: { [attributeCode]: value }
            });
            this.productRequestChange$.next();
        }
    }

    private retrieveOptionValue(attribute: Attribute, value: number): ProductConfigurationConfigurableOptionValue {
        if (!this.optionValuesMap.has(value)) {
            runInInjectionContext(
                Injector.create({
                    providers: [
                        {
                            provide: VALUE_TOKEN,
                            useValue: value
                        },
                        {
                            provide: ATTRIBUTE_TOKEN,
                            useValue: attribute
                        },
                        {
                            provide: ProductConfigurationConfigurableOptionValue,
                            useClass: ProductConfigurationConfigurable.optionInstance
                        },
                        {
                            provide: ProductConfigurationConfigurable,
                            useValue: this
                        }
                    ],
                    parent: this.injector
                }),
                () => {
                    this.optionValuesMap.set(value, inject(ProductConfigurationConfigurableOptionValue));
                }
            );
        }
        return this.optionValuesMap.get(value)!;
    }

    /**
     * Helper method getting logic for selection change
     */
    private getSelectionLogic(
        wanted: Partial<NProductConfiguration.Configurable.SelectionLogic> = {}
    ): NProductConfiguration.Configurable.SelectionLogic {
        return { ...this.config!.selection.default!, ...wanted };
    }

    /**
     * Adjust URL path to match current product
     */
    private saveMetaState() {
        if (this.options.metaIntegration) {
            const model = this.product$.value;

            // replace URL path if needed; only after any changes
            if (this.initiated) {
                const urlModelType = this.config?.urlModel;
                if (urlModelType && urlModelType !== 'none') {
                    if (model) {
                        const urlModel = urlModelType === 'parent' ? model.parent : model;
                        const fragment = this.hashStorageService.getHash();
                        const path = urlModel.data!.url;
                        if (this.location.path() !== path) {
                            const url = urlModel.data!.url + (fragment?.length > 3 ? '#' + fragment : '');
                            this.location.replaceState(url);
                        }
                    }
                }
            }

            // replace meta model if needed
            const metaModelType = this.config?.metaModel;
            if (metaModelType && metaModelType !== 'none') {
                const metaModel = (metaModelType === 'parent' ? model.parent : model) as Product<IProductDataV3 & IModelDataV3WithUrl>;
                if (metaModel && this.meta.model !== metaModel) {
                    this.meta.model = metaModel;
                }
            }
        }
    }
}
